import type * as Sentry from '@sentry/vue';
import {
    MetricOptions,
    noopTelemetry,
    Telemetry,
    TelemetrySeverity,
} from '@studoku/shared';
import type { App } from 'vue';
import type { Router } from 'vue-router';

/**
 * The browser's provider adapter — the only file in the SPA that names the
 * observability vendor. Everything else takes a {@link Telemetry} (via
 * `useTelemetry()` or a parameter) and stays provider-agnostic.
 *
 * The `studoku-frontend` DSN is not a secret (it ships in the bundle), but
 * reporting is limited to production builds so local dev, unit tests and e2e
 * runs — all of which serve from the Vite dev server — never send events.
 */
const DSN =
    'https://0e8ef66bbc02262cac6bc63128ecbe7e@o4511837428908032.ingest.us.sentry.io/4511838399299584';

type SentryModule = typeof import('@sentry/vue');

const SEVERITY: Record<TelemetrySeverity, Sentry.SeverityLevel> = {
    debug: 'debug',
    info: 'info',
    warning: 'warning',
    error: 'error',
    fatal: 'fatal',
};

function toMetricOptions(options: MetricOptions | undefined) {
    return options && { unit: options.unit, attributes: options.attributes };
}

/** Exported so the adapter can be asserted against a real client in tests. */
export function sentryTelemetry(sentry: SentryModule): Telemetry {
    return {
        // Attributes map to `tags`, not Sentry's `attributes`: the latter is
        // only serialized onto logs, metrics and spans, never onto events.
        captureError: (error, attributes) => {
            sentry.captureException(error, { tags: attributes });
        },
        captureMessage: (message, severity = 'info', attributes) => {
            sentry.captureMessage(message, {
                level: SEVERITY[severity],
                tags: attributes,
            });
        },
        setUser: (user) => {
            sentry.setUser(user);
        },
        span: (name, callback, attributes) =>
            sentry.startSpan({ name, attributes }, callback),
        metrics: {
            count: (name, value, options) =>
                sentry.metrics.count(name, value, toMetricOptions(options)),
            gauge: (name, value, options) =>
                sentry.metrics.gauge(name, value, toMetricOptions(options)),
            distribution: (name, value, options) =>
                sentry.metrics.distribution(
                    name,
                    value,
                    toMetricOptions(options),
                ),
        },
    };
}

function initSentry(
    app: App,
    router: Router,
    beforeInit: () => void,
): Promise<Telemetry> {
    return import('@sentry/vue').then((sentry) => {
        beforeInit();
        sentry.init({
            app,
            dsn: DSN,
            environment: import.meta.env.MODE,
            release: import.meta.env.VITE_STUDOKU_VERSION,

            integrations: [
                sentry.browserTracingIntegration({ router }),
                sentry.replayIntegration(),
                sentry.consoleLoggingIntegration({ levels: ['warn', 'error'] }),
            ],

            // Tracing. The API is same-origin, so only relative `/api` calls
            // need trace headers propagated.
            tracesSampleRate: 1.0,
            tracePropagationTargets: [/^\/api\//],

            // Session Replay: sample a slice of ordinary sessions, and keep
            // every session that produced an error.
            replaysSessionSampleRate: 0.1,
            replaysOnErrorSampleRate: 1.0,

            // Send structured logs.
            enableLogs: true,
        });
        return sentryTelemetry(sentry);
    });
}

function whenIdle(fn: () => void): void {
    if (typeof requestIdleCallback === 'function') {
        requestIdleCallback(fn, { timeout: 3000 });
    } else {
        setTimeout(fn, 0);
    }
}

interface BufferedTelemetry {
    telemetry: Telemetry;
    /** Point the facade at the real provider and replay buffered calls. */
    activate: (live: Telemetry) => void;
    /** Feed an out-of-band error (e.g. an uncaught one) through the buffer. */
    capture: (error: unknown) => void;
}

// A facade that buffers captureError/captureMessage/setUser until a provider is
// activated, then replays them; span/metrics run through a no-op until then.
export function bufferedTelemetry(): BufferedTelemetry {
    const noop = noopTelemetry();
    let live: Telemetry | null = null;
    const pending: ((t: Telemetry) => void)[] = [];

    const run = (replay: (t: Telemetry) => void) =>
        live ? replay(live) : pending.push(replay);

    return {
        capture: (error) => run((t) => t.captureError(error)),
        activate: (t) => {
            live = t;
            for (const replay of pending) replay(t);
            pending.length = 0;
        },
        telemetry: {
            captureError: (error, attributes) =>
                run((t) => t.captureError(error, attributes)),
            captureMessage: (message, severity, attributes) =>
                run((t) => t.captureMessage(message, severity, attributes)),
            setUser: (user) => run((t) => t.setUser(user)),
            span: (name, callback, attributes) =>
                (live ?? noop).span(name, callback, attributes),
            metrics: {
                count: (name, value, options) =>
                    (live ?? noop).metrics.count(name, value, options),
                gauge: (name, value, options) =>
                    (live ?? noop).metrics.gauge(name, value, options),
                distribution: (name, value, options) =>
                    (live ?? noop).metrics.distribution(name, value, options),
            },
        },
    };
}

// Keep the SDK out of the eager entry: hand back the facade now, load and init
// Sentry when idle. Temporary handlers route uncaught errors through the buffer
// until Sentry's own take over, so nothing is lost before it loads.
function deferredTelemetry(app: App, router: Router): Telemetry {
    const { telemetry, activate, capture } = bufferedTelemetry();

    const onError = (e: ErrorEvent) => capture(e.error ?? e.message);
    const onRejection = (e: PromiseRejectionEvent) => capture(e.reason);
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    app.config.errorHandler = (error) => capture(error);

    whenIdle(() =>
        initSentry(app, router, () => {
            window.removeEventListener('error', onError);
            window.removeEventListener('unhandledrejection', onRejection);
            app.config.errorHandler = undefined;
        }).then(activate),
    );

    return telemetry;
}

/**
 * Returns the telemetry to hand to the app: a no-op when reporting is disabled
 * for this build, otherwise a facade that lazily loads and initializes Sentry.
 */
export function installTelemetry(app: App, router: Router): Telemetry {
    if (import.meta.env.SSR || !import.meta.env.PROD) {
        return noopTelemetry();
    }
    return deferredTelemetry(app, router);
}
